import { useEffect } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import ContractLayout from "../components/ContractLayout";
import SEOHead from "../components/SEOHead";
import useIsMobile from "../hooks/useIsMobile";
import MODELS from "./models";
import {
  buildArticleSchema,
  buildBreadcrumbSchema,
  buildFAQSchema,
  injectSchemas,
} from "../utils/structuredData";
import { trackModelPdfDownload, trackModelToGeneratorClick } from "../utils/analytics";
import { getContractIdFromSlug } from "../utils/contractSlugs";

const SITE_URL = "https://generator-contracte.com";

// Render a token: string | { text, bold? } | { link, text }
function renderToken(token, key) {
  if (typeof token === "string") return <span key={key}>{token}</span>;
  if (token.link) {
    return (
      <Link
        key={key}
        to={token.link}
        style={{ color: "#c45d3e", fontWeight: 600, textDecoration: "none" }}
      >
        {token.text}
      </Link>
    );
  }
  if (token.bold) {
    return (
      <strong key={key} style={{ color: "#2d2418", fontWeight: 700 }}>
        {token.text}
      </strong>
    );
  }
  return <span key={key}>{token.text}</span>;
}

// Render a paragraph: string | token[]
function renderParagraph(p, key, baseStyle) {
  if (typeof p === "string") {
    return (
      <p key={key} style={baseStyle}>
        {p}
      </p>
    );
  }
  return (
    <p key={key} style={baseStyle}>
      {p.map((tok, i) => renderToken(tok, i))}
    </p>
  );
}

export default function ModelLandingPage() {
  const location = useLocation();
  const isMobile = useIsMobile();

  const slug = location.pathname.replace(/^\//, "").replace(/\/$/, "");
  const config = MODELS[slug];

  useEffect(() => {
    if (!config || config.status !== "live") return;
    const pageUrl = `${SITE_URL}/${config.slug}`;
    return injectSchemas(
      [
        buildArticleSchema({
          title: config.seoTitle,
          description: config.seoDescription,
          date: "2026-01-01",
          url: pageUrl,
        }),
        buildBreadcrumbSchema([
          { name: "Acasă", item: `${SITE_URL}/` },
          { name: config.crumbLabel || "Model PDF", item: pageUrl },
        ]),
        buildFAQSchema(config.faq),
      ],
      "model-schema"
    );
  }, [config]);

  if (!config || config.status !== "live") {
    return <Navigate to="/" replace />;
  }

  const pageUrl = `${SITE_URL}/${config.slug}`;
  // contract_type comes from the generator this page points at. config.slug is
  // the landing page's own slug ("model-contract-inchiriere-pdf"), not a
  // contract route, so it cannot be resolved to a contract id.
  const contractType = getContractIdFromSlug(config.generatorPath);
  const pdfHref = `/models/${config.pdfFilename}`;
  const previewHref = `/models/${config.pdfFilename.replace(/\.pdf$/, "-preview.png")}`;

  const paragraphStyle = {
    fontSize: "15px",
    color: "#4a3f35",
    lineHeight: 1.75,
    margin: "0 0 16px 0",
  };

  const h2Style = {
    fontFamily: "'Playfair Display', serif",
    fontSize: "24px",
    fontWeight: 700,
    color: "#2d2418",
    margin: "0 0 16px 0",
    lineHeight: 1.3,
  };

  const sectionCardStyle = {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: isMobile ? "28px 20px" : "40px 32px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
    border: "1px solid #e8e0d4",
    marginBottom: "24px",
  };

  const sectionWrap = {
    maxWidth: "860px",
    margin: "0 auto",
    padding: "0 24px",
  };

  const primaryButtonStyle = {
    display: "inline-block",
    padding: "16px 28px",
    background: "linear-gradient(135deg, #c45d3e 0%, #a84832 100%)",
    color: "#fff",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: 700,
    fontFamily: "'DM Sans', sans-serif",
    textDecoration: "none",
    boxShadow: "0 4px 14px rgba(196,93,62,0.3)",
    border: "none",
    cursor: "pointer",
  };

  const secondaryButtonStyle = {
    display: "inline-block",
    padding: "16px 28px",
    backgroundColor: "transparent",
    color: "#c45d3e",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: 700,
    fontFamily: "'DM Sans', sans-serif",
    textDecoration: "none",
    border: "2px solid #c45d3e",
  };

  return (
    <>
      <SEOHead
        title={config.seoTitle}
        description={config.seoDescription}
        canonical={pageUrl}
        ogType={config.ogType || "article"}
      />
      <ContractLayout title={config.h1} subtitle={config.subtitle}>
        {/* Breadcrumbs + primary CTAs */}
        <div style={{ ...sectionWrap, paddingTop: "24px", paddingBottom: "8px" }}>
          <nav style={{ fontSize: "13px", color: "#8a7e70", marginBottom: "20px" }}>
            <Link to="/" style={{ color: "#8a7e70", textDecoration: "none" }}>Acasă</Link>
            <span style={{ margin: "0 8px" }}>›</span>
            <span style={{ color: "#4a3f35" }}>{config.crumbLabel || "Model PDF"}</span>
          </nav>

          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "12px",
              marginBottom: "24px",
            }}
          >
            <a
              href={pdfHref}
              download={config.pdfFilename}
              style={{ ...primaryButtonStyle, textAlign: "center" }}
              onClick={() =>
                trackModelPdfDownload(contractType, "pdf_landing_page", "hero", config.slug)
              }
            >
              {config.pdfLabel}
            </a>
            <Link
              to={config.generatorPath}
              style={{ ...secondaryButtonStyle, textAlign: "center" }}
              onClick={() =>
                trackModelToGeneratorClick(config.slug, contractType, "hero")
              }
            >
              {config.generatorCtaLabel}
            </Link>
          </div>
        </div>

        {/* Preview slot */}
        <div style={sectionWrap}>
          <a
            href={pdfHref}
            download={config.pdfFilename}
            aria-label={config.previewCaption}
            onClick={() =>
              trackModelPdfDownload(contractType, "pdf_landing_page", "preview_image", config.slug)
            }
            style={{
              ...sectionCardStyle,
              padding: 0,
              overflow: "hidden",
              display: "block",
              backgroundColor: "#fdfbf7",
              cursor: "pointer",
            }}
          >
            <img
              src={previewHref}
              alt={config.previewCaption}
              loading="lazy"
              style={{
                display: "block",
                width: "100%",
                height: "auto",
              }}
            />
          </a>
        </div>

        {/* What's included */}
        <div style={sectionWrap}>
          <div style={sectionCardStyle}>
            <h2 style={h2Style}>{config.whatsIncluded.title}</h2>
            <p style={paragraphStyle}>{config.whatsIncluded.intro}</p>
            <ul style={{ margin: 0, paddingLeft: "20px", listStyleType: "disc" }}>
              {config.whatsIncluded.items.map((it, i) => (
                <li
                  key={i}
                  style={{
                    fontSize: "15px",
                    color: "#4a3f35",
                    lineHeight: 1.75,
                    marginBottom: "6px",
                  }}
                >
                  {it}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Educational sections */}
        <div style={sectionWrap}>
          {config.educationalSections.map((section, i) => (
            <div key={i} style={sectionCardStyle}>
              <h2 style={h2Style}>{section.title}</h2>
              {section.paragraphs.map((p, j) => renderParagraph(p, j, paragraphStyle))}
            </div>
          ))}
        </div>

        {/* Why personalize */}
        <div style={sectionWrap}>
          <div
            style={{
              ...sectionCardStyle,
              backgroundColor: "#2d2418",
              color: "#fdfbf7",
              border: "none",
            }}
          >
            <h2 style={{ ...h2Style, color: "#fdfbf7" }}>{config.whyPersonalizeSection.title}</h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "20px",
                marginBottom: "28px",
              }}
            >
              {config.whyPersonalizeSection.items.map((item, i) => (
                <div key={i}>
                  <div
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "17px",
                      fontWeight: 700,
                      color: "#e8a88a",
                      marginBottom: "6px",
                    }}
                  >
                    {item.title}
                  </div>
                  <div style={{ fontSize: "14px", color: "#b8a890", lineHeight: 1.65 }}>
                    {item.body}
                  </div>
                </div>
              ))}
            </div>
            <Link
              to={config.generatorPath}
              style={primaryButtonStyle}
              onClick={() =>
                trackModelToGeneratorClick(config.slug, contractType, "why_personalize")
              }
            >
              {config.whyPersonalizeSection.ctaLabel}
            </Link>
          </div>
        </div>

        {/* FAQ */}
        <div style={sectionWrap}>
          <div style={sectionCardStyle}>
            <h2 style={h2Style}>Întrebări frecvente</h2>
            {config.faq.map((item, i) => (
              <div
                key={i}
                style={{
                  paddingBottom: "20px",
                  marginBottom: i < config.faq.length - 1 ? "20px" : 0,
                  borderBottom: i < config.faq.length - 1 ? "1px solid #e8e0d4" : "none",
                }}
              >
                <h3
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#2d2418",
                    margin: "0 0 8px 0",
                  }}
                >
                  {item.q}
                </h3>
                <p style={{ ...paragraphStyle, margin: 0 }}>{item.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Related links */}
        <div style={sectionWrap}>
          <div style={sectionCardStyle}>
            <h2 style={{ ...h2Style, fontSize: "20px" }}>{config.relatedLinks.title}</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {config.relatedLinks.items.map((it, i) => (
                <Link
                  key={i}
                  to={it.to}
                  style={{
                    padding: "14px 18px",
                    borderRadius: "10px",
                    border: "1px solid #e8e0d4",
                    color: "#4a3f35",
                    textDecoration: "none",
                    fontSize: "14px",
                    fontWeight: 600,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>{it.label}</span>
                  <span style={{ color: "#c45d3e" }}>→</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div style={{ ...sectionWrap, paddingBottom: "40px" }}>
          <div
            style={{
              padding: "20px 24px",
              backgroundColor: "rgba(196, 93, 62, 0.06)",
              borderRadius: "12px",
              border: "1px solid rgba(196, 93, 62, 0.12)",
            }}
          >
            <p style={{ fontSize: "13px", color: "#8a7e70", margin: 0, lineHeight: 1.6 }}>
              <strong style={{ color: "#4a3f35" }}>Disclaimer:</strong> Modelul și informațiile de pe
              această pagină au caracter orientativ și nu constituie consultanță juridică sau fiscală.
              Pentru situații particulare (contracte de valoare mare, litigii, spații cu destinații
              speciale) se recomandă consultarea unui avocat sau consultant fiscal.
            </p>
          </div>
        </div>
      </ContractLayout>
    </>
  );
}
